import { Box, Code, Group, Text } from '@mantine/core';
import { match } from 'ts-pattern';
import { EvaluationValue, EvaluationValueKind } from '../../types';
import { BooleanIndicator, OutputText } from './styles';
import { formatEvaluationValue } from '../../utils';

export type OutputValueDisplayProps = {
  value: EvaluationValue;
  maxLength?: number;
  variant?: 'card' | 'inline';
  emptyLabel?: string;
  textSx?: (theme: any) => Record<string, any>;
};

export const OutputValueDisplay = ({
  value,
  maxLength = 120,
  variant = 'card',
  emptyLabel = 'Empty output',
  textSx,
}: OutputValueDisplayProps) => {
  const formatted = formatEvaluationValue(value);
  const display = formatted || '';
  const isLarge = display.length > maxLength;
  const preview = isLarge ? `${display.slice(0, maxLength)}…` : display;

  if (variant === 'inline') {
    return (
      <OutputText size="xs" sx={textSx} lineClamp={1}>
        {preview.trim().length > 0 ? preview : emptyLabel}
      </OutputText>
    );
  }

  return match(value.kind)
    .with(EvaluationValueKind.Boolean, () => (
      <Group spacing="xs" align="center">
        <BooleanIndicator
          color={
            formatted === 'True'
              ? 'var(--mantine-color-green-6)'
              : 'var(--mantine-color-red-6)'
          }
        />
        <Text
          fz="xs"
          fw={400}
          sx={(theme) => ({ fontFamily: theme.fontFamilyMonospace })}
        >
          {formatted}
        </Text>
      </Group>
    ))
    .with(EvaluationValueKind.Number, () => (
      <Text
        fz="sm"
        fw={400}
        sx={(theme) => ({ fontFamily: theme.fontFamilyMonospace })}
      >
        {formatted}
      </Text>
    ))
    .with(EvaluationValueKind.Empty, () => (
      <Text
        fz="xs"
        c="dimmed"
        fw={400}
        sx={(theme) => ({ fontFamily: theme.fontFamilyMonospace })}
      >
        {emptyLabel}
      </Text>
    ))
    .with(EvaluationValueKind.String, () => (
      <Box>
        {isLarge && (
          <Text fz="xs" c="dimmed">
            Preview truncated
          </Text>
        )}
        <OutputText fz="xs" lineClamp={isLarge ? 3 : undefined}>
          {preview}
        </OutputText>
      </Box>
    ))
    .with(EvaluationValueKind.Array, EvaluationValueKind.Object, () => (
      <Box>
        {isLarge && (
          <Text fz="xs" c="dimmed">
            Preview truncated
          </Text>
        )}
        <Code
          block
          fz="xs"
          sx={(theme) => ({
            fontFamily: theme.fontFamilyMonospace,
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
          })}
        >
          {preview}
        </Code>
      </Box>
    ))
    .exhaustive();
};
